// @flow

import * as React from 'react';

import { connect } from 'react-redux';
import { createSelector } from 'reselect';

import {
  selectCartConfigurations,
  type CartConfiguration,
  type ProductKind,
} from '@gandi/cart';
import { notifyDanger } from '@gandi/notifications';
import { useTranslator } from '@gandi/translate';
import { Base, PopoverIcon } from '@gandi/ui';

import { useFeatureFlags } from '../../../../../../hooks/useFeatureFlag';
import { upsertConfiguration } from '../../../../../../modules/configurations';
import { selectShopCurrent } from '../../../../../../modules/organization/selectors';
import type { FullState, Dispatch } from '../../../../../../types';

import { AutoRenewBlock } from '../../../../AutoRenewBlock';

import ControlledAsyncSwitch from './ControlledAsyncSwitch';

import styles from './Autorenew.scss';

type ProductType = string;
type Process = string;

type OwnProps = {
  kind: ProductKind,
  productType: ProductType,
  process: Process,
};

type StateProps = {
  orgAutorenew: boolean,
  configuration: ?CartConfiguration,
};

type DispatchProps = {
  dispatch: Dispatch,
};

type Props = {
  ...OwnProps,
  ...StateProps,
  ...DispatchProps,
};

function Autorenew({
  dispatch,
  kind,
  orgAutorenew,
  configuration,
  productType,
  process,
}: Props) {
  const { __ } = useTranslator();
  const { FF_CART_OVERVIEW_NDS } = useFeatureFlags();

  // Legacy style: move the spread at inside the updateConfigAutorenew
  const {
    // $FlowFixMe[incompatible-type]
    payload: { autorenew: configAutorenew = null, ...restPayload } = {},
    // $FlowFixMe[incompatible-type]
    id: configId = null,
  } = configuration || {};

  const updateConfigAutorenew = (value: ?boolean) =>
    upsertConfiguration({
      id: configId,
      product_type: productType,
      process,
      // $FlowExpectedError[cannot-spread-inexact] - payload definition is inexact
      payload: { autorenew: value, ...restPayload },
    });

  const [checked, setChecked] = React.useState<boolean>(
    (FF_CART_OVERVIEW_NDS && kind === 'MailboxStandAloneCreate') ||
      !!configAutorenew,
  );

  React.useEffect(() => {
    // Configuration's `autorenew` setting is redundant.
    // Clear it as a best-effort towards data-cleanliness.
    if (orgAutorenew && configAutorenew) {
      dispatch(updateConfigAutorenew(null)).catch(() => {
        // pass
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgAutorenew, configAutorenew]);

  const onChange = async (val: boolean) => {
    setChecked(val);
    try {
      await dispatch(updateConfigAutorenew(val));
    } catch (err) {
      setChecked(!val);
      dispatch(
        notifyDanger({
          message: __(
            'Unable to update automatic renewal status for these products at this time.',
          ),
        }),
      );
      throw err;
    }
  };

  if (FF_CART_OVERVIEW_NDS) {
    const autoRenewBlockProps = orgAutorenew
      ? {
          popoverText: __(
            'This option has been activated on the organization settings.' +
              ' Each new product is automatically renewed when expiring.',
          ),
        }
      : {
          onChange,
        };

    return (
      <AutoRenewBlock
        name={kind}
        id={`autorenewStatus-${kind}`}
        checked={checked}
        {...autoRenewBlockProps}
      />
    );
  }

  // Legacy style
  if (orgAutorenew) {
    return (
      <Base
        flex
        flexGrow1
        flexJustifyBetween
        flexAlignContentCenter
        className={styles.flexAlignItemsInherit}
      >
        <span className={styles.label}>{__('Automatic renewal')}</span>
        <PopoverIcon
          id="popover-product-autorenew-success"
          icon="successWire"
          iconTheme="success"
          className={styles.PopoverIcon}
          title={__('Automatic Renewal Enabled')}
        >
          {__(
            'This option has been activated on the organization settings.' +
              ' Each new product is automatically renewed when expiring.',
          )}
        </PopoverIcon>
      </Base>
    );
  }

  return (
    <Base>
      <Base flex flexAlignItemsCenter gap="xsmall">
        <Base flex flexAlignItemsCenter gap="xxsmall">
          <Base tagName="label" weight="regular" htmlFor={kind} interactive>
            {__('Automatic renewal')}
          </Base>
          <PopoverIcon
            id="popover-product-autorenew-info"
            icon="infoWire"
            title={__('Set Automatic Renewal')}
          >
            {__(
              'When reaching the expiration date,' +
                'we will automatically renew products for you.' +
                " This is handy for products you don't want to lose." +
                ' The renewal will be done on the saved credit card' +
                ' or on the related prepaid account.',
            )}
          </PopoverIcon>
        </Base>
        <ControlledAsyncSwitch
          name={kind}
          checked={checked}
          onChange={onChange}
        >
          {__('Automatic renewal')}
        </ControlledAsyncSwitch>
      </Base>
    </Base>
  );
}

const selectOrgAutorenew = (state: FullState): boolean => {
  const org = selectShopCurrent(state, false);
  return !!org?.autorenew;
};

const selectConfigurationFactory = ({
  productType,
  process,
}: {
  productType: string,
  process: string,
}): ((state: FullState) => ?CartConfiguration) =>
  createSelector(
    [selectCartConfigurations],
    (configurations: Array<CartConfiguration>) =>
      configurations.find(
        config =>
          config.product_type === productType && config.process === process,
      ),
  );

function makeMapStateToProps(_: FullState, { productType, process }: OwnProps) {
  const selectConfiguration = selectConfigurationFactory({
    productType,
    process,
  });
  return function mapStateToProps(state: FullState) {
    return {
      orgAutorenew: selectOrgAutorenew(state),
      configuration: selectConfiguration(state),
    };
  };
}

// $FlowFixMe[not-an-object]
// $FlowFixMe[underconstrained-implicit-instantiation]
export default (connect(makeMapStateToProps)(Autorenew): Class<
  // $FlowFixMe[cannot-resolve-name]
  ConnectedComponent<OwnProps, (_0: Props) => React.Node>,
> &
  (Props => React.Node));
